import { useEffect, useState, useRef, useMemo } from "react";
import { Alert, Row, Col, Card, Button, Container } from "react-bootstrap";
import AppointmentBook from "components/AppointmentBook/AppointmentBook";
import PhoneSessionTypeModal from "../phone-session-type-modal";
import OrchidProFullProfile from "components/orchid-pro/full-profile/OrchidProFullProfile";
import GroupSeriesCard from "components/GroupSession/components/group-series-card";
import BlogCard from "components/Blogging/components/blog-card/BlogCard";
import Loading from "components/Loading";
import NextButton from "components/pagination-buttons/components/next-button";
import PreviousButton from "components/pagination-buttons/components/previous-button";
import ContentHeader from "components/content-header/ContentHeader";
import Footer from "components/footer/Footer";
import StickyFooter from "components/sticky-footer/";
import queryOrchidProById from "services/queryOrchidProById";
import queryAllGroupSeries from "services/queryAllGroupSeries";
import getAppointmentTypes from "services/scheduler/querySessionTypesByProId";

import { apiHostAndPath } from "utils/api";
import {
  getOrchidProNameAndAllGraduateDegreesString,
  getOrchidProTypeStringFromNewArr,
} from "utils/formatter/orchidPro";
import { useParams, useLocation } from "react-router-dom";
import getClasses from "utils/getClasses";
import styles from "./InfoPage.module.scss";
import queryBlogEntryByPro from "services/queryBlogEntryByPro";
import { useUser } from "contexts/UserContext";
import { useAuthModals } from "contexts/auth-modals-context";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import { AmplitudePageName, track } from "utils/amplitude";
import { useTimeOnPageContext } from "contexts/time-on-page";
import { Helmet } from "react-helmet";
import useImageDimensions from "hooks/useImageDimensions";
import getCanonicalLink from "utils/getCanonicalLink";
import getOrchidProStructuredData from "./getOrchidProStructuredData";
import formatSessionDuration from "utils/formatSessionDuration";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "pro-public-profile-page";

const InfoPage = ({ pageHeightCallback }) => {
  const { setPage } = useTimeOnPageContext();
  useEffect(() => setPage(AMPLITUDE_PAGE_NAME), [setPage]);

  const { user } = useUser();
  const { openAuthModals } = useAuthModals();
  const { isPhonePortrait } = usePhonePortraitContext();
  const { id_or_custom_url } = useParams<{
    id_or_custom_url?: string;
  }>();

  const [orchidPro, setOrchidPro] = useState<OrchidPro>();
  const [error, setError] = useState<any>();
  const [busy, setBusy] = useState(false);

  const [appointmentTypes, setAppointmentTypes] = useState([] as any);
  const [selectedType, setSelectedType] = useState<SessionType>();
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [groupSeries, setGroupSeries] = useState([] as any);
  const [blogPosts, setBlogPosts] = useState([] as any);

  const [showBottomSessionTypes, setShowBottomSessionTypes] = useState(false);

  // page start
  const [apptPage, setApptPage] = useState(0);
  const [groupPage, setGroupPage] = useState(0);
  const [blogPage, setBlogPage] = useState(0);

  const apptPageMax = Math.ceil(appointmentTypes.length / 3);
  const groupPageMax = Math.ceil(groupSeries.length / 2);
  const blogPageMax = Math.ceil(blogPosts.length / 2);

  const apptsPerPage = appointmentTypes.slice(3 * apptPage, 3 * (apptPage + 1));
  const groupsPerPage = groupSeries.slice(2 * groupPage, 2 * (groupPage + 1));
  const blogsPerPage = blogPosts.slice(2 * blogPage, 2 * (blogPage + 1));

  const ref = useRef<HTMLDivElement | null>(null);

  const location = useLocation();
  const { sessionType } = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      sessionType: query.get("session_type") || "",
    };
  }, [location.search]);

  const openSessionsModal = () => {
    if (!user?.signup_form_finished) {
      openAuthModals();
    } else {
      setShowSessionsModal(true);
    }
  };

  useEffect(() => {
    async function initData() {
      setBusy(true);
      if (!id_or_custom_url) return;
      const orchidPro = await queryOrchidProById(id_or_custom_url, true);
      setOrchidPro(orchidPro);

      if (orchidPro) {
        const appointmentTypes = await getAppointmentTypes(
          orchidPro.cognito_sub,
        );
        setAppointmentTypes(appointmentTypes);

        const groupSeries = await queryAllGroupSeries();
        const groupSeriesCurrPro = groupSeries.filter((groupSeries) =>
          groupSeries.speakers.includes(orchidPro.cognito_sub),
        );
        setGroupSeries(groupSeriesCurrPro);

        const blogPosts = await queryBlogEntryByPro(orchidPro.cognito_sub);
        setBlogPosts(blogPosts);
      }

      if (ref.current) {
        pageHeightCallback(ref.current.clientHeight);
      }
    }

    initData()
      .catch(setError)
      .finally(() => setBusy(false));
    // eslint-disable-next-line
  }, []);

  const _renderAppointmentCard = (orchidPro, appointmentType, index) => {
    return (
      <Card className={getClasses("w-100", styles["appoint-type-card"])}>
        <Card.Body>
          <Card.Text className="bd-16 mb-0">{appointmentType.label}</Card.Text>
          <Card.Text className="rg-14 mb-0 gray01">
            {formatSessionDuration(appointmentType.length_min)}
          </Card.Text>
        </Card.Body>
        <Card.Footer className="background-transparent border-0">
          {appointmentType.price === 0 ? (
            <Card.Text className="rg-16 green mb-2">Free</Card.Text>
          ) : user?.signup_form_finished ? (
            <Card.Text className="rg-16 mb-2">
              ${appointmentType.price}
            </Card.Text>
          ) : (
            <Card.Text className="rg-16 mb-2">
              Log in to see the price
            </Card.Text>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              track(AMPLITUDE_PAGE_NAME, "choose-session-button", "click");
              setSelectedType(appointmentType);
              openSessionsModal();
            }}
          >
            Choose
          </Button>
        </Card.Footer>
      </Card>
    );
  };

  const profileImage = useMemo(
    () =>
      orchidPro
        ? apiHostAndPath(
            `/api/users/orchid_pro/profile_image/${orchidPro.cognito_sub}`,
          )
        : "",
    [orchidPro],
  );

  const { width: profileImageWidth, height: profileImageHeight } =
    useImageDimensions(profileImage);

  const profileImageAltText = useMemo(
    () =>
      orchidPro
        ? `Photo of ${getOrchidProNameAndAllGraduateDegreesString(orchidPro)}`
        : "",
    [orchidPro],
  );

  const pageTitle = useMemo(() => {
    if (!orchidPro) return "";

    let orchidProType = getOrchidProTypeStringFromNewArr(orchidPro);
    if (orchidProType?.toLowerCase() === "social worker") {
      orchidProType = "Therapist";
    }
    return `${getOrchidProNameAndAllGraduateDegreesString(
      orchidPro,
    )} | ${orchidProType} | Orchid`;
  }, [orchidPro]);

  const structuredData = useMemo(
    () => getOrchidProStructuredData(orchidPro),
    [orchidPro],
  );

  return orchidPro ? (
    busy ? (
      <Loading />
    ) : (
      <>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={orchidPro.biography} />

          <link
            rel="canonical"
            href={getCanonicalLink(
              `/professional-search/${
                orchidPro.custom_url ?? orchidPro.cognito_sub
              }`,
            )}
          />

          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>

          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={orchidPro.biography} />
          <meta property="og:image" content={profileImage} />
          <meta property="og:image:url" content={profileImage} />
          <meta property="og:image:secure_url" content={profileImage} />
          <meta property="og:image:alt" content={profileImageAltText} />
          <meta property="og:image:width" content={profileImageWidth} />
          <meta property="og:image:height" content={profileImageHeight} />
          <meta property="og:image:type" content="image/jpeg" />

          <meta name="twitter:title" content={pageTitle} />
          <meta name="twitter:description" content={orchidPro.biography} />
          <meta name="twitter:image" content={profileImage} />
          <meta name="twitter:image:src" content={profileImage} />
          <meta name="twitter:image:alt" content={profileImageAltText} />
          <meta name="twitter:card" content="summary_large_image" />

          <meta
            property="profile:first_name"
            content={orchidPro.preferred_first_name ?? orchidPro.first_name}
          />
          <meta property="profile:last_name" content={orchidPro.last_name} />
        </Helmet>

        <ContentHeader
          title={getOrchidProNameAndAllGraduateDegreesString(orchidPro)}
        />
        <Container
          className={getClasses(
            isPhonePortrait
              ? styles["full-profile--phone"]
              : styles["full-profile"],
          )}
          ref={ref}
        >
          {error && <Alert variant="danger">{error.toString()}</Alert>}
          {!isPhonePortrait ? (
            <Row>
              <Col md={7} className={"pl-0"}>
                <OrchidProFullProfile
                  orchidPro={orchidPro}
                  amplitudePageName={AMPLITUDE_PAGE_NAME}
                />
              </Col>
              <Col xs={12} md={5}>
                <div className={styles["calendar-container"]}>
                  <h4 className="rg-24 deep-blue questrial mb-4">
                    Book a session
                  </h4>
                  <AppointmentBook
                    orchidPro={orchidPro}
                    showThreeRows={true}
                    appointmentType={appointmentTypes.find(
                      (type) => type.label === sessionType,
                    )}
                    amplitudePageName={AMPLITUDE_PAGE_NAME}
                    amplitudeComponentName="book-a-session-modal"
                  />
                </div>
              </Col>
            </Row>
          ) : (
            <OrchidProFullProfile
              orchidPro={orchidPro}
              amplitudePageName={AMPLITUDE_PAGE_NAME}
            />
          )}

          {orchidPro.can_have_appointment &&
            orchidPro.can_accept_payment &&
            appointmentTypes.filter((type) => !type.invite_only).length > 0 && (
              <>
                <hr />
                <Row
                  className={getClasses(
                    "justify-content-between",
                    !isPhonePortrait
                      ? styles["full-profile__section-title"]
                      : styles["full-profile__section-title--phone"],
                  )}
                >
                  <h4 className="rg-24 questrial deep-blue my-3">
                    Book a session
                  </h4>

                  <span className="mb-3">
                    <PreviousButton
                      disabled={apptPage === 0}
                      onClick={() => {
                        track(
                          AMPLITUDE_PAGE_NAME,
                          "next-session-types-button",
                          "click",
                        );
                        setApptPage((page) => page - 1);
                      }}
                    />
                    <NextButton
                      disabled={apptPage >= apptPageMax - 1}
                      onClick={() => {
                        track(
                          AMPLITUDE_PAGE_NAME,
                          "previous-session-types-button",
                          "click",
                        );
                        setApptPage((page) => page + 1);
                      }}
                    />
                  </span>
                </Row>

                <Row>
                  {apptsPerPage
                    ?.filter((type) => !type.invite_only)
                    ?.map((appointmentType, index) => {
                      return (
                        <Col
                          md={4}
                          xs={12}
                          key={index}
                          className={getClasses(
                            !isPhonePortrait && "pl-0 d-flex",
                          )}
                        >
                          {_renderAppointmentCard(
                            orchidPro,
                            appointmentType,
                            index,
                          )}
                        </Col>
                      );
                    })}
                </Row>
              </>
            )}

          {orchidPro.can_accept_payment && groupSeries.length !== 0 && (
            <>
              <hr className={styles["full-profile__hr"]} />
              <div
                className={
                  !isPhonePortrait
                    ? styles["full-profile__section-title"]
                    : styles["full-profile__section-title--phone"]
                }
              >
                <Row className="justify-content-between">
                  <h4 className="rg-24 questrial my-3">
                    {orchidPro.preferred_first_name
                      ? orchidPro.preferred_first_name
                      : orchidPro.first_name}
                    's Group Sessions
                  </h4>
                  <span className="mb-3">
                    <PreviousButton
                      disabled={groupPage === 0}
                      onClick={() => {
                        track(
                          AMPLITUDE_PAGE_NAME,
                          "previous-group-sessions-button",
                          "click",
                        );
                        setGroupPage((page) => page - 1);
                      }}
                    />
                    <NextButton
                      disabled={groupPage >= groupPageMax - 1}
                      onClick={() => {
                        track(
                          AMPLITUDE_PAGE_NAME,
                          "next-group-sessions-button",
                          "click",
                        );
                        setGroupPage((page) => page + 1);
                      }}
                    />
                  </span>
                </Row>
              </div>
              <Row>
                {groupsPerPage
                  .slice()
                  .sort((a, b) =>
                    a.next_session.start > b.next_session.start ? 1 : -1,
                  )
                  .filter(
                    (el) =>
                      el.id !== 400 &&
                      el.id !== 405 &&
                      el.id !== 404 &&
                      el.id !== 394 &&
                      el.id !== 396,
                  )
                  .map((groupSeries) => {
                    return (
                      <Col
                        md={6}
                        lg={6}
                        className={getClasses(
                          "d-flex mb-4",
                          !isPhonePortrait && "pl-0",
                        )}
                        key={groupSeries.id}
                      >
                        <GroupSeriesCard groupSeries={groupSeries} />
                      </Col>
                    );
                  })}
              </Row>
            </>
          )}

          {blogPosts.length !== 0 && (
            <>
              <hr />
              <Row
                className={getClasses(
                  "justify-content-between",
                  !isPhonePortrait
                    ? styles["full-profile__section-title"]
                    : styles["full-profile__section-title--phone"],
                )}
              >
                <h4 className="rg-24 questrial my-3">
                  {orchidPro.preferred_first_name
                    ? orchidPro.preferred_first_name
                    : orchidPro.first_name}
                  's Blog
                </h4>
                <span className="mb-3">
                  <PreviousButton
                    disabled={blogPage === 0}
                    onClick={() => {
                      track(
                        AMPLITUDE_PAGE_NAME,
                        "previous-blogs-page-button",
                        "click",
                      );
                      setBlogPage((page) => page - 1);
                    }}
                  />
                  <NextButton
                    disabled={blogPage >= blogPageMax - 1}
                    onClick={() => {
                      track(
                        AMPLITUDE_PAGE_NAME,
                        "next-blogs-page-button",
                        "click",
                      );
                      setBlogPage((page) => page + 1);
                    }}
                  />
                </span>
              </Row>
              <Row>
                {blogsPerPage
                  .slice()
                  .sort((a, b) =>
                    a.create_datetime > b.create_datetime ? 1 : -1,
                  )
                  .map((blog) => {
                    return (
                      <Col
                        md={6}
                        lg={6}
                        className={getClasses(
                          "d-flex mb-4",
                          !isPhonePortrait && "pl-0",
                        )}
                        key={blog.id}
                      >
                        <BlogCard
                          blog={blog}
                          amplitudePageName={AMPLITUDE_PAGE_NAME}
                        />
                      </Col>
                    );
                  })}
              </Row>
            </>
          )}
        </Container>
        {!isPhonePortrait ? (
          <Footer AMPLITUDE_PAGE_NAME={AMPLITUDE_PAGE_NAME} />
        ) : showBottomSessionTypes ? (
          <StickyFooter className="p-0">
            <PhoneSessionTypeModal
              show={showBottomSessionTypes}
              onHide={() => {
                setShowBottomSessionTypes(false);
                setShowSessionsModal(false);
              }}
              setSelectedType={setSelectedType}
              setShowSessionsModal={openSessionsModal}
              appointmentTypes={appointmentTypes}
              amplitudePageName={AMPLITUDE_PAGE_NAME}
            />
          </StickyFooter>
        ) : (
          <StickyFooter>
            {appointmentTypes.filter((type) => !type.invite_only).length > 0 ? (
              <>
                {appointmentTypes
                  .filter((type) => !type.invite_only)
                  .findIndex((type) => type.price === 0) !== -1 && (
                  <Button
                    variant="primary"
                    className={styles["appointment-schedule-button--phone"]}
                    onClick={() => {
                      track(
                        AMPLITUDE_PAGE_NAME,
                        "schedule-free-intro-call-button",
                        "click",
                      );

                      setSelectedType(
                        appointmentTypes
                          .filter((type) => !type.invite_only)
                          .find((type) => type.price === 0),
                      );
                      openSessionsModal();
                    }}
                  >
                    Schedule a free intro call
                  </Button>
                )}
                <Button
                  variant="link"
                  className="w-100 rg-15"
                  style={{
                    padding: "0.625rem 0.313rem",
                    marginTop: "0.25rem",
                  }}
                  onClick={() => {
                    track(
                      AMPLITUDE_PAGE_NAME,
                      "book-a-session-button",
                      "click",
                    );
                    setShowBottomSessionTypes(true);
                  }}
                >
                  Book a session
                </Button>
              </>
            ) : (
              <p className="gray01 rg-13-footnote mb-0 py-3 d-flex justify-content-center">
                No available sessions
              </p>
            )}
          </StickyFooter>
        )}
        {showSessionsModal && (
          <AppointmentBook
            orchidPro={orchidPro}
            appointmentType={selectedType}
            onHide={() => setShowSessionsModal(false)}
            show={showSessionsModal}
            amplitudePageName={AMPLITUDE_PAGE_NAME}
            amplitudeComponentName={"book-a-session-modal"}
          />
        )}
      </>
    )
  ) : null;
};

export default InfoPage;
